import { IStudent } from "../../Api/Interface/api.interface";

/**
 * Resolve a student's grade name, tolerant of where the data lives:
 *   1. the eager-loaded `grade` relation (authoritative — always correct),
 *   2. the (permission-scoped) grade picklist map, keyed by grade_id,
 *   3. a generic "Grade {id}" so a valid grade_id never renders as "—",
 *   4. "" only when there is genuinely no grade.
 *
 * This is the single source of truth for grade display so Assign Class, the
 * student summary and the profile all agree.
 */
export function gradeLabelOf(
  student: Pick<IStudent, "grade" | "grade_id"> | null | undefined,
  gradeMap?: Map<number, string>
): string {
  if (!student) return "";
  if (student.grade?.grade) return student.grade.grade;
  if (student.grade_id != null) {
    return gradeMap?.get(student.grade_id) || `Grade ${student.grade_id}`;
  }
  return "";
}

/**
 * Canonical Study Mode handling for students. Two values only — `online` /
 * `in_person` — shown as "Online" / "In Person". Legacy spellings ("Offline",
 * "InPerson", …) normalize here so no student surface ever prints "Offline".
 * Mirrors backend App\Support\StudyMode.
 */
export const STUDY_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: "online", label: "Online" },
  { value: "in_person", label: "In Person" },
];

/** Any spelling → canonical token: "online" | "in_person" | "hybrid" | "". */
export function normalizeStudyModeValue(raw?: string | null): string {
  const m = String(raw ?? "").trim().toLowerCase();
  if (["online", "virtual", "remote"].includes(m)) return "online";
  if (["offline", "inperson", "in_person", "in-person", "in person", "physical", "onsite"].includes(m)) return "in_person";
  if (["hybrid", "blended"].includes(m)) return "hybrid";
  return "";
}

/** Human label — "Online" / "In Person" / "Hybrid" / "" (never "Offline"). */
export function studyModeLabelOf(raw?: string | null): string {
  switch (normalizeStudyModeValue(raw)) {
    case "online": return "Online";
    case "in_person": return "In Person";
    case "hybrid": return "Hybrid";
    default: return "";
  }
}

/**
 * User-facing label for a CLASS mode. A Hybrid class is spoken about as
 * "Online + In-Person" everywhere except technical/admin surfaces. Prefer
 * this over `studyModeLabelOf` when rendering a class card, schedule row,
 * calendar tile, PDF, or any other class-scoped context.
 */
export function classModeLabelOf(raw?: string | null): string {
  switch (normalizeStudyModeValue(raw)) {
    case "online": return "Online";
    case "in_person": return "In-Person";
    case "hybrid": return "Online + In-Person";
    default: return "";
  }
}

export const isInPersonMode = (raw?: string | null): boolean => normalizeStudyModeValue(raw) === "in_person";

/** Same relation-first strategy for the student's campus/location. */
export function locationLabelOf(
  student: Pick<IStudent, "location" | "location_id"> | null | undefined,
  locationMap?: Map<number, { location_name: string }>
): string {
  if (!student) return "";
  if (student.location?.location_name) return student.location.location_name;
  if (student.location_id != null) {
    return locationMap?.get(student.location_id)?.location_name || "";
  }
  return "";
}
